// FE-SCRM-055 — Settings → Workflow flags section.
//
// Per-entity toggle list. Each row PATCHes
// ``/api/v1/entities/<id>/broker-settings`` with the flag and surfaces
// an inline confirmation. ``enable_win_loss_interviews`` is the only
// live flag in v1; stub rows live alongside so the UI shape doesn't
// change when subsequent flags ride the same surface.

import { Toggle } from "@/design-system";

import type { BrokerSettings } from "../../settings/types";


type FlagKey = "enable_win_loss_interviews";


interface FlagRow {
  key: FlagKey;
  title: string;
  description: string;
}


const FLAG_ROWS: readonly FlagRow[] = [
  {
    key: "enable_win_loss_interviews",
    title: "Win/loss interviews",
    description:
      "When an Opportunity moves to Closed Lost, automatically "
      + "spawn a 30-day win/loss interview activity assigned to "
      + "the producer.",
  },
];


interface Props {
  settings: BrokerSettings | null | undefined;
  onPatch: (input: Partial<BrokerSettings>) => void;
  isPending: boolean;
}


export function WorkflowFlagsSection({
  settings, onPatch, isPending,
}: Props): JSX.Element {
  return (
    <section
      data-testid="settings-workflow-flags"
      className="flex flex-col gap-3"
    >
      <header className="flex flex-col gap-1">
        <h2 className="text-base font-semibold text-text-primary">
          Workflow flags
        </h2>
        <p className="text-xs text-text-secondary">
          Per-entity automation toggles. Changes apply immediately
          for every producer on this brokerage.
        </p>
      </header>

      <ul className="flex flex-col gap-2">
        {FLAG_ROWS.map((row) => {
          const enabled = Boolean(settings?.[row.key]);
          return (
            <li
              key={row.key}
              data-testid={`flag-row-${row.key}`}
              className="flex items-start justify-between gap-4 rounded-md border border-border-subtle bg-raised p-3"
            >
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-text-primary">
                  {row.title}
                </span>
                <p className="text-xs text-text-secondary">
                  {row.description}
                </p>
              </div>
              <Toggle
                checked={enabled}
                disabled={isPending}
                data-testid={`flag-toggle-${row.key}`}
                onChange={(next) =>
                  onPatch({ [row.key]: next } as Partial<BrokerSettings>)}
              />
            </li>
          );
        })}
      </ul>
    </section>
  );
}
